import React, {useCallback, useContext, useEffect, useState} from "react";

import "./AchForm.scss";
import {AchAccountInput} from "../inputs/AchAccountInput/AchAccountInput";
import {AmountInput} from "../inputs/AmountInput/AmountInput";
import {AchRoutingNumberInput} from "../inputs/AchRoutingNumberInput/AchRoutingNumberInput";
import {AchFirstNameInput} from "../inputs/AchFirstNameInput/AchFirstNameInput";
import {Invoice} from "../../../models/invoice.model";
import {InvoiceContext} from "../../../context/InvoiceContext";
import {Company} from "../../../models/company.model";
import {CompanyContext} from "../../../context/CompanyContext";
import {AchPayment} from "../../../models/ach.payment.model";
import {CONSTANTS} from "../../../common/constants";
import {AchAccountTypeSelect} from "../inputs/AchAccountTypeSelect/AchAccountTypeSelect";
import {AchAccountConfirmInput} from "../inputs/AchAccounConfirmtInput/AchAccountConfirmInput";
import {AchCompanyNameInput} from "../inputs/AchCompanyNameInput/AchCompanyNameInput";
import {AchLastNameInput} from "../inputs/AchLastNameInput/AchLastNameInput";
import {OptionContext} from "../../../context/OptionContext";

interface AchFormProps {
    onSetAchPayment: (achPayment: AchPayment) => void;
    onSetAchPaymentValid: (isValid: boolean) => void;
}

export const AchForm: React.FunctionComponent<AchFormProps> = (props) => {
    const invoice: Invoice = useContext(InvoiceContext);
    const company: Company = useContext(CompanyContext);
    const optionContext = useContext(OptionContext);
    const [achAccount, setAchAccount] = useState<string>('');
    const [isAchAccountValid, setAchConfirmAccountValid] = useState<boolean>(false);
    const [achConfirmAccount, setAchConfirmAccount] = useState<string>('');
    const [isAchConfirmAccountValid, setAchAccountValid] = useState<boolean>(false);
    const [achRoutingNumber, setAchRoutingNumber] = useState<string>('');
    const [isAchRoutingNumberValid, setAchRoutingNumberValid] = useState<boolean>(false);
    const [achFirstName, setAchFirstName] = useState<string>('');
    const [isAchFirstNameValid, setAchFirstNameValid] = useState<boolean>(false);
    const [achLastName, setAchLastName] = useState<string>('');
    const [isAchLastNameValid, setAchLastNameValid] = useState<boolean>(false);
    const [achAmount, setAchAmount] = useState<string>(optionContext.getPaymentAmountWithoutCardFeeRecovery());
    const [isAchAmountValid, setAchAmountValid] = useState<boolean>(true);
    const [achAccountType, setAchAccountType] = useState<string>(CONSTANTS.ACH_ACCOUNT_TYPE.PERSONAL_CHECKING_ACCOUNT);
    const [isAchCompanyNameVisible, setAchCompanyNameVisible] = useState<boolean>(false);
    const [achCompanyName, setAchCompanyName] = useState<string>('');
    const [isAchCompanyNameValid, setAchCompanyNameValid] = useState<boolean>(false);

    const isFormValid = useCallback(() => {
        return isAchAccountValid && isAchAmountValid && (isAchCompanyNameVisible ? isAchCompanyNameValid : isAchFirstNameValid && isAchLastNameValid) && isAchRoutingNumberValid && isAchConfirmAccountValid;
    }, [isAchAccountValid, isAchAmountValid, isAchLastNameValid, isAchFirstNameValid, isAchRoutingNumberValid, isAchConfirmAccountValid, isAchCompanyNameValid, isAchCompanyNameVisible])

    const updateAchAccountType = (accountType: string) => {
        setAchAccountType(accountType);
        if (accountType === CONSTANTS.ACH_ACCOUNT_TYPE.PERSONAL_SAVING_ACCOUNT || accountType === CONSTANTS.ACH_ACCOUNT_TYPE.PERSONAL_CHECKING_ACCOUNT || accountType === CONSTANTS.ACH_ACCOUNT_TYPE.CARDCONNECT_SAVING_ACCOUNT || accountType === CONSTANTS.ACH_ACCOUNT_TYPE.CARDCONNECT_CHECKING_ACCOUNT) {
            setAchCompanyNameVisible(false);
            setAchCompanyName('');
        } else {
            setAchCompanyNameVisible(true);
            setAchFirstName('');
            setAchLastName('');
        }
    }

    useEffect(() => {
        props.onSetAchPaymentValid(isFormValid());
    }, [isAchAccountValid, isAchAmountValid, isAchFirstNameValid, isAchLastNameValid, isAchRoutingNumberValid, isAchConfirmAccountValid, isAchCompanyNameValid, isAchCompanyNameVisible]);

    useEffect(() => {
        const achPayment: AchPayment = {
            amount: achAmount,
            account: achAccount,
            routing: achRoutingNumber,
            payment_account_type: achAccountType
        } as AchPayment;
        if (isAchCompanyNameVisible) {
            achPayment.company_name = achCompanyName;
        } else {
            achPayment.first_name = achFirstName;
            achPayment.last_name = achLastName;
            achPayment.middle_name = '';
        }
        props.onSetAchPayment(achPayment);
    }, [achAmount, achAccount, achRoutingNumber, achFirstName, achLastName, achCompanyName, achAccountType]);

    return <div className="ach-container">
        <form>
            <div className="form-group">
                {!isAchCompanyNameVisible && <div className="input-container">
                    <label>First Name:</label>
                    <AchFirstNameInput achFirstName={achFirstName} onSetAchFirstName={setAchFirstName}
                                       onSetIsAchFirstNameValid={setAchFirstNameValid}/>
                </div>}
                {!isAchCompanyNameVisible && <div className="input-container">
                    <label>Last name:</label>
                    <AchLastNameInput achLastName={achLastName} onSetAchLastName={setAchLastName}
                                      onSetIsAchLastNameValid={setAchLastNameValid}/>
                </div>}
                {isAchCompanyNameVisible && <div className="input-container">
                    <label>Company Name on Account:</label>
                    <AchCompanyNameInput achCompanyName={achCompanyName} onSetAchCompanyName={setAchCompanyName}
                                         onSetIsAchCompanyNameValid={setAchCompanyNameValid}/>
                </div>}

                <div className="select-container">
                    <label>Account Type:</label>
                    <AchAccountTypeSelect achAccountType={achAccountType} onSetAchAccountType={updateAchAccountType}/>
                </div>
            </div>
            <div className="form-group">
                <div className="input-container">
                    <label>Amount ({company.currency_code}):</label>
                    <AmountInput amount={achAmount} maxAmount={achAmount} onSetAmount={setAchAmount}
                                 onSetIsAmountValid={setAchAmountValid}/>
                </div>

                <div className="input-container">
                    <label>Routing Number:</label>
                    <AchRoutingNumberInput achRoutingNumber={achRoutingNumber}
                                           onSetAchRoutingNumber={setAchRoutingNumber}
                                           onSetIsAchRoutingNumberValid={setAchRoutingNumberValid}/>
                </div>
            </div>

            <div className="form-group">
                <div className="input-container">
                    <label>Account Number:</label>
                    <AchAccountInput achAccount={achAccount} onSetAchAccount={setAchAccount}
                                     onSetIsAchAccountValid={setAchAccountValid}/>
                </div>

                <div className="input-container">
                    <label>Confirm Account Number:</label>
                    <AchAccountConfirmInput achAccount={achAccount} achAccountConfirm={achConfirmAccount}
                                            onSetAchAccountConfirm={setAchConfirmAccount}
                                            onSetIsAchAccountConfirmValid={setAchConfirmAccountValid}/>
                </div>
            </div>
        </form>
    </div>
}
